import React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { ShoppingBag, Crown, MessageCircle, Instagram, Facebook, Youtube, Twitter, Globe } from "lucide-react";

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, twitter: Twitter, website: Globe };

export const StoreHero = ({ store, accent, productCount }) => (
  <div className="relative">
    <div className="h-40 md:h-60 bg-cover bg-center relative" style={store.banner_url ? { backgroundImage: `url(${store.banner_url})` } : { backgroundColor: accent }}>
      <div className="absolute inset-0 bg-black/30" />
    </div>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative -mt-16 md:-mt-20 bg-white rounded-2xl shadow-lg border border-gray-100 p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-white border-4 border-white shadow-md overflow-hidden flex-shrink-0 flex items-center justify-center">
          {store.logo_url ? (
            <img src={store.logo_url} alt={store.business_name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: accent }}>
              <span className="text-2xl font-bold text-white">{store.business_name?.charAt(0)?.toUpperCase() || "S"}</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-heading text-xl md:text-2xl font-bold text-gray-900 truncate" data-testid="store-name">{store.business_name}</h1>
            {store.is_featured && (
              <Badge className="bg-amber-100 text-amber-700 border-0" data-testid="store-featured-badge"><Crown className="w-3 h-3 mr-1" />Featured Store</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500">by {store.name}</p>
          {store.description && <p className="text-sm text-gray-600 mt-2 line-clamp-2" data-testid="store-description">{store.description}</p>}
        </div>
        <Badge className="self-start md:self-center border-0 text-white" style={{ backgroundColor: accent }}>
          <ShoppingBag className="w-3 h-3 mr-1" />{productCount} Products
        </Badge>
      </div>
    </div>
  </div>
);

export const StoreNav = ({ store, accent, activePage }) => {
  if (!store.pages?.length) return null;
  const linkCls = (active) => `px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${active ? "text-white" : "text-gray-600 hover:bg-gray-100"}`;
  return (
    <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 flex gap-2 overflow-x-auto" data-testid="store-pages-nav">
      <Link to={`/store/${store.store_slug}`} className={linkCls(!activePage)} style={!activePage ? { backgroundColor: accent } : {}} data-testid="store-nav-home">Shop</Link>
      {store.pages.map((p) => (
        <Link key={p.slug} to={`/store/${store.store_slug}/p/${p.slug}`} className={linkCls(activePage === p.slug)} style={activePage === p.slug ? { backgroundColor: accent } : {}} data-testid={`store-nav-${p.slug}`}>
          {p.title}
        </Link>
      ))}
    </nav>
  );
};

export const StoreFooter = ({ store, accent }) => {
  const socials = Object.entries(store.social || {}).filter(([k, v]) => v && SOCIAL_ICONS[k]);
  return (
    <footer className="bg-gray-900 text-gray-300 mt-12" data-testid="store-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col md:flex-row gap-6 md:items-center justify-between">
        <div>
          <p className="font-heading text-lg font-bold text-white">{store.business_name}</p>
          <p className="text-sm text-gray-400">Powered by M Ventures — Buy, Earn, Refer & Repeat</p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm">
          {(store.pages || []).map((p) => (
            <Link key={p.slug} to={`/store/${store.store_slug}/p/${p.slug}`} className="hover:text-white">{p.title}</Link>
          ))}
        </div>
        {socials.length > 0 && (
          <div className="flex gap-3">
            {socials.map(([k, url]) => {
              const Icon = SOCIAL_ICONS[k];
              return (
                <a key={k} href={url.startsWith("http") ? url : `https://${url}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-transform hover:scale-110" style={{ backgroundColor: accent }} data-testid={`store-social-${k}`}>
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </footer>
  );
};

export const WhatsAppButton = ({ number, storeName }) => {
  if (!number) return null;
  const full = number.length === 10 ? `91${number}` : number;
  const text = encodeURIComponent(`Hi ${storeName}, I found your store on M Ventures and have a question.`);
  return (
    <a href={`https://wa.me/${full}?text=${text}`} target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#25D366] text-white shadow-xl flex items-center justify-center transition-transform hover:scale-110" aria-label="Chat on WhatsApp" data-testid="store-whatsapp-btn">
      <MessageCircle className="w-7 h-7" />
    </a>
  );
};
